// Treasury refund-requests module endpoint functions.

import { adminApi } from '@core/api/httpClient'
import { REFUND_REQUESTS_ENDPOINT_PATHS } from './refundsEndpointPaths'
import type {
  ApproveRefundPayloadDto,
  RefundDetailDto,
  RefundRowDto,
  RefundStatusCountsDto,
  RefundsListResponseDto,
  RejectRefundPayloadDto,
} from './refundsContracts'
import type {
  ApproveRefundInput,
  GetRefundsParams,
  RefundDetail,
  RefundRow,
  RefundStatusCounts,
  RefundsListResponse,
  RejectRefundInput,
} from '../types/refundsTypes'
import {
  parseRefundDetail,
  parseRefundRow,
  parseRefundStatusCounts,
  parseRefundsListResponse,
} from './refundsParser'

type WireListResponse = RefundsListResponseDto | { data: RefundsListResponseDto }
type WireDetailResponse = RefundDetailDto | { data: RefundDetailDto }
type WireRowResponse = RefundRowDto | { data: RefundRowDto }
type WireStatusCounts = RefundStatusCountsDto | { data: RefundStatusCountsDto }

function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export async function getRefunds(params: GetRefundsParams): Promise<RefundsListResponse> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = String(params.page)
  if (params.limit) queryParams.limit = String(params.limit)
  if (params.refundState) queryParams.refundState = params.refundState
  if (params.refundMethod) queryParams.refundMethod = params.refundMethod
  if (params.fromDate) queryParams.fromDate = params.fromDate
  if (params.toDate) queryParams.toDate = params.toDate
  if (params.investorId) queryParams.investorId = params.investorId
  if (params.investorName) queryParams.investorName = params.investorName
  if (params.investorEmail) queryParams.investorEmail = params.investorEmail
  if (params.sortOrder) queryParams.sortOrder = params.sortOrder

  const { data } = await adminApi.get<WireListResponse>(REFUND_REQUESTS_ENDPOINT_PATHS.LIST, {
    params: queryParams,
  })
  return parseRefundsListResponse(unwrap(data))
}

export async function getRefundStatusCounts(): Promise<RefundStatusCounts> {
  const { data } = await adminApi.get<WireStatusCounts>(
    REFUND_REQUESTS_ENDPOINT_PATHS.STATUS_COUNTS,
  )
  return parseRefundStatusCounts(unwrap(data))
}

export async function getRefundDetail(id: string): Promise<RefundDetail> {
  const { data } = await adminApi.get<WireDetailResponse>(REFUND_REQUESTS_ENDPOINT_PATHS.DETAIL(id))
  return parseRefundDetail(unwrap(data))
}

export async function approveRefund(input: ApproveRefundInput): Promise<RefundRow> {
  const payload: ApproveRefundPayloadDto = {
    idempotencyKey: input.idempotencyKey,
  }
  if (input.notes) payload.notes = input.notes
  const { data } = await adminApi.post<WireRowResponse>(
    REFUND_REQUESTS_ENDPOINT_PATHS.APPROVE(input.id),
    payload,
  )
  return parseRefundRow(unwrap(data))
}

export async function rejectRefund(input: RejectRefundInput): Promise<RefundRow> {
  const payload: RejectRefundPayloadDto = {
    reason: input.reason,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireRowResponse>(
    REFUND_REQUESTS_ENDPOINT_PATHS.REJECT(input.id),
    payload,
  )
  return parseRefundRow(unwrap(data))
}
